import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ZinMessage } from '@tnl/zin-react'
import { useAutoClearTimer } from '@hooks/useAutoClearTimer'

export interface FormErrorProps {
  /** Optional single message to display when no array provided */
  message?: string
  /** Optional array of validation errors to display */
  errors?: ReadonlyArray<string>
  /** Controls visibility; defaults to true if there is content */
  visible?: boolean
  /** Severity type for the banner(s) */
  type?: 'error' | 'warning' | 'info' | 'success'
  /** Optional class name for the container */
  className?: string
  /** Auto-clear options for the error message */
  autoClear?: {
    /** Duration in milliseconds before auto-clearing (default: 5000) */
    duration?: number
    /** Whether to enable auto-clear (default: false) */
    enabled?: boolean
    /** Callback when auto-clear occurs */
    onClear?: () => void
  }
}

type FormErrorBannerType = NonNullable<FormErrorProps['type']>

const FormErrorZinFallback = ({ heading }: { heading: string }) => (
  <div
    role="alert"
    className="form-error__message-fallback">
    {heading}
  </div>
)

/** One banner row: ZinMessage with a local fallback if Stencil throws (CHP2-14792). */
const FormErrorZinRow = ({ heading, type }: { heading: string; type: FormErrorBannerType }) => (
  <ErrorBoundary fallback={<FormErrorZinFallback heading={heading} />}>
    <ZinMessage
      heading={heading}
      type={type}
    />
  </ErrorBoundary>
)

/**
 * FormError
 * Lightweight wrapper that renders one or more ZinMessage rows for
 * step-level validation or API errors.
 */
export const FormError: React.FC<FormErrorProps> = ({ message, errors, visible, type = 'error', className = '', autoClear }) => {
  const hasErrors = Array.isArray(errors) && errors.length > 0
  const shouldShow = visible ?? Boolean(message || hasErrors)

  // Memoize the onClear callback to prevent infinite re-renders
  const handleClear = React.useCallback(() => {
    autoClear?.onClear?.()
  }, [autoClear?.onClear])

  // Auto-clear timer for error messages
  const {
    start: startTimer,
    stop: stopTimer,
    isActive,
    timeRemaining,
  } = useAutoClearTimer({
    duration: autoClear?.duration ?? 5000,
    onClear: handleClear,
    autoStart: false,
  })

  // Start timer when error appears and auto-clear is enabled
  React.useEffect(() => {
    if (autoClear?.enabled && shouldShow) {
      startTimer()
    } else {
      stopTimer()
    }
  }, [autoClear?.enabled, shouldShow])

  if (!shouldShow) return null

  if (hasErrors) {
    // Filter out undefined, null, or empty error messages
    const validErrors = errors!.filter((err) => err && err.trim().length > 0)

    if (validErrors.length === 0) {
      return null
    }

    return (
      <div className={`form-error ${className}`.trim()}>
        {validErrors.map((err) => (
          <FormErrorZinRow
            key={err}
            heading={err}
            type={type}
          />
        ))}
        {autoClear?.enabled && isActive && <div className="form-error__countdown text-xs text-gray-500 mt-2">Auto-clearing in {Math.ceil(timeRemaining / 1000)}s</div>}
      </div>
    )
  }

  // Ensure message is a non-empty string before rendering
  if (message && message.trim().length > 0) {
    return (
      <div className={`form-error ${className}`.trim()}>
        <FormErrorZinRow
          heading={message}
          type={type}
        />
        {autoClear?.enabled && isActive && <div className="form-error__countdown text-xs text-gray-500 mt-2">Auto-clearing in {Math.ceil(timeRemaining / 1000)}s</div>}
      </div>
    )
  }

  return null
}

export default FormError
